import React, { useEffect, useState } from "react";
import { DialogWrapMedium } from "../../../controls/components/dialogWrap/DialogWrap";
import {
	UserFormServiceButtonClose,
	UserFormServiceButtonCreated,
} from "../../MyBooking/styled/DesStyled";
import usePartnerDetail from "../hook/useGetUserDetail";
import useUpdateImagePartner from "../hook/useUpdateImagePartner";
import {
	PartnerbuttonRemoteImage,
	PartnerImage,
	PartnerImageInner,
	PartnerImageWrap,
} from "../../PersonalInformation/styled/PersonInfoStyled";
import { PutPartnerStationLandingContent, uploadAvatarMutiImage } from "../../../../services/PartnerService";
import { resolveAssetUrl } from "../../../../utils/assetUrl";
import { NearbyAttractionDraft, NearbyAttractionsAdminEditor } from "./DialogUpdatePartnerAdmin";

export default function DialogImagesPartner(props: {
	openDialog: boolean;
	setOpenDialog: (value: boolean) => void;
	partnerId: string;
	reload: () => void;
}) {
	const { loadUpdateImage } = useUpdateImagePartner({ reload: props.reload });
	const { userDetail } = usePartnerDetail({ id: props.partnerId, openDialog: props.openDialog });

	const [imagesEnabled, setImagesEnabled] = useState(false);
	const [meetImages, setMeetImages] = useState<(string | File)[]>([]);
	const [nearbyAttractionsContent, setNearbyAttractionsContent] = useState<NearbyAttractionDraft[]>([]);

	useEffect(() => {
		if (!userDetail) return;
		setImagesEnabled(userDetail.imagesEnabled ?? false);
		const legacy = (userDetail.images || []) as string[];
		const hasMeetData = userDetail.meetStationImages && userDetail.meetStationImages.length > 0;
		const hasNearbyData = userDetail.nearbyStationImages && userDetail.nearbyStationImages.length > 0;
		const isLegacyOnly = !hasMeetData && !hasNearbyData;
		const meet = hasMeetData ? userDetail.meetStationImages : (isLegacyOnly ? legacy : []);
		setMeetImages([...meet]);
		const existingAttractions = (userDetail as any).nearbyAttractionsContent || [];
		if (existingAttractions.length > 0) {
			setNearbyAttractionsContent(existingAttractions);
		} else {
			const nearby = hasNearbyData ? userDetail.nearbyStationImages : (isLegacyOnly ? legacy : []);
			if (nearby.length > 0) {
				setNearbyAttractionsContent(nearby.map((img: string) => ({
					image: img,
					title: "",
					walkingMinutes: 5,
					description: "",
				})));
			}
		}
	}, [userDetail]);

	const handleSave = async () => {
		const resolvedAttractions = await Promise.all(
			nearbyAttractionsContent.map(async (item) => {
				if (item.image instanceof File) {
					const results = await uploadAvatarMutiImage([item.image]);
					const url = results?.[0]?.url || results?.[0]?.path || "";
					return { ...item, image: url };
				}
				return { ...item, image: item.image as string };
			})
		);

		const nearbyImages = resolvedAttractions
			.map((item) => item.image)
			.filter((url) => url.trim());

		await Promise.all([
			loadUpdateImage(props.partnerId, meetImages, nearbyImages, imagesEnabled),
			PutPartnerStationLandingContent(props.partnerId, {
				nearbyAttractionsContent: resolvedAttractions,
			}),
		]);
		props.setOpenDialog(false);
	};

	return (
		<DialogWrapMedium
			login={true}
			open={props.openDialog}
			onClose={() => props.setOpenDialog(false)}
			title="Edit Station Landing Page"
			actions={
				<>
					<UserFormServiceButtonClose onClick={() => props.setOpenDialog(false)}>
						Cancel
					</UserFormServiceButtonClose>
					<UserFormServiceButtonCreated onClick={handleSave}>
						Save Changes
					</UserFormServiceButtonCreated>
				</>
			}>

			{/* Master toggle */}
			<div style={{ marginBottom: 20 }}>
				<label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
					<input
						type="checkbox"
						checked={imagesEnabled}
						onChange={(e) => setImagesEnabled(e.target.checked)}
						style={{ width: 18, height: 18, cursor: "pointer" }}
					/>
					<span style={{ fontWeight: 600 }}>Enable Station Landing Section</span>
				</label>
				<p style={{ margin: "6px 0 0 28px", fontSize: 12, color: "#546e7a" }}>
					When off, the entire "Meet your Station" and "Nearby Attractions" section is hidden on the station page.
				</p>
			</div>

			{imagesEnabled && (
				<>
					{/* SECTION 1: Meet your Station */}
					<SectionBlock>
						<SectionHeader color="#1565c0">Meet your Station</SectionHeader>

						<FieldLabel>Photo (1 image)</FieldLabel>
						<UploadSingleImage image={meetImages[0] ?? null} setImage={(img) => setMeetImages(img ? [img] : [])} />
					</SectionBlock>

					{/* SECTION 2: Nearby Attractions */}
					<SectionBlock>
						<SectionHeader color="#1565c0">Nearby Attractions</SectionHeader>
						<FieldLabel>Each item shows as a card on the station page (max 3)</FieldLabel>
						<NearbyAttractionsAdminEditor
							items={nearbyAttractionsContent}
							onChange={setNearbyAttractionsContent}
						/>
					</SectionBlock>
				</>
			)}
		</DialogWrapMedium>
	);
}

/* Small layout helpers */
function SectionBlock({ children }: { children: React.ReactNode }) {
	return (
		<div
			style={{
				border: "1px solid #e0e0e0",
				borderRadius: 8,
				padding: "16px 20px",
				marginBottom: 20,
			}}>
			{children}
		</div>
	);
}

function SectionHeader({ color, children }: { color: string; children: React.ReactNode }) {
	return (
		<div style={{ fontWeight: 700, fontSize: 15, color, marginBottom: 14 }}>
			{children}
		</div>
	);
}

function FieldLabel({ children }: { children: React.ReactNode }) {
	return (
		<div style={{ fontWeight: 600, fontSize: 13, color: "#374151", marginBottom: 6 }}>
			{children}
		</div>
	);
}

function UploadSingleImage(props: {
	image: string | File | null;
	setImage: (value: (string | File) | null) => void;
}) {
	const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) props.setImage(file);
		e.target.value = "";
	};

	return (
		<PartnerImageWrap>
			{props.image && (
				<PartnerImageInner>
					<PartnerImage
						src={typeof props.image === "string" ? resolveAssetUrl(props.image) : URL.createObjectURL(props.image)}
						alt="meet-station"
					/>
					<PartnerbuttonRemoteImage onClick={() => props.setImage(null)}>×</PartnerbuttonRemoteImage>
				</PartnerImageInner>
			)}
			<label style={{ cursor: "pointer" }}>
				<input
					type="file"
					accept="image/*"
					style={{ display: "none" }}
					onChange={handleUpload}
				/>
				<div
					style={{
						width: 120,
						height: 120,
						border: "2px dashed #ccc",
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						borderRadius: 8,
						color: "#9e9e9e",
						fontSize: 13,
						gap: 6,
					}}>
					<span style={{ fontSize: 24 }}>{props.image ? "↻" : "+"}</span>
					<span>{props.image ? "Replace" : "Add photo"}</span>
				</div>
			</label>
		</PartnerImageWrap>
	);
}
